/**
 * Setzt den Markennamen auf den rechtlichen Seiten in die Akzentschrift.
 *
 * Impressum, Widerruf, Versand, AGB und Datenschutz kommen aus den
 * Einstellungen des Shops, nicht aus dem Theme. Dort gibt es keine Moeglichkeit,
 * einzelnen Woertern eine Schrift zuzuweisen: der Editor wirft Auszeichnungen
 * beim Speichern wieder weg, und beim naechsten Bearbeiten des Textes waeren
 * sie ohnehin verschwunden. Bei den Texten, die Shopify selbst erzeugt - etwa
 * der Datenschutzerklaerung - laesst sich der Wortlaut ueberhaupt nicht
 * anfassen.
 *
 * Deshalb geschieht es hier, und zwar unabhaengig davon, wie der Name im Text
 * geschrieben steht: gefunden wird auch "DivinePrint", gesetzt wird immer die
 * Wortmarke "DIVINEPRINT". So steht der Name auf allen rechtlichen Seiten
 * gleich da, ohne dass jemand die Quelltexte angleichen muesste.
 *
 * Ausgenommen bleibt der Name als Teil einer Adresse - "mail@divineprint.de"
 * oder "www.divineprint.de". Dort ist er keine Marke, sondern ein Stueck
 * Technik, das genau so geschrieben gehoert, wie man es eintippt.
 *
 * Gearbeitet wird im Textknoten selbst, damit die Auszeichnung des Textes -
 * Absaetze, Fettungen, Verweise - unangetastet bleibt.
 */
const NAME = 'DIVINEPRINT';
const MARKE = /divineprint/i;

/**
 * Die Stelle des naechsten echten Markennamens im Text - oder -1.
 *
 * @param {string} text
 * @returns {number}
 */
function naechsteStelle(text) {
  let ab = 0;

  for (;;) {
    const treffer = text.slice(ab).search(MARKE);
    if (treffer < 0) return -1;

    const stelle = ab + treffer;
    const davor = text.slice(Math.max(0, stelle - 1), stelle);
    const danach = text.slice(stelle + NAME.length, stelle + NAME.length + 2);

    /*
     * Ein Zeichen davor, das eine Adresse fortsetzt, oder eine Endung wie
     * ".de" dahinter: dann steckt der Name in einer Mail- oder Webadresse.
     * Ein Punkt am Satzende stoert dabei nicht - ihm folgt ein Leerzeichen
     * und kein Buchstabe.
     */
    const inAdresse = /[\w@./]/.test(davor) || danach.startsWith('@') || /^\.[a-z]/i.test(danach);
    if (!inAdresse) return stelle;

    ab = stelle + NAME.length;
  }
}

/**
 * Ersetzt jedes Vorkommen im Text eines einzelnen Knotens.
 *
 * Beide Schreibweisen sind gleich lang, deshalb genuegt zum Abtrennen die
 * Laenge des Namens.
 *
 * @param {Text} knoten
 */
function zeichneAus(knoten) {
  let rest = knoten;

  for (;;) {
    const stelle = naechsteStelle(rest.textContent);
    if (stelle < 0) return;

    // Erst den Namen abtrennen, dann alles dahinter - uebrig bleibt genau er.
    const mitte = rest.splitText(stelle);
    rest = mitte.splitText(NAME.length);

    const marke = document.createElement('span');
    marke.className = 'dp-marke';
    marke.textContent = NAME;
    mitte.parentNode?.replaceChild(marke, mitte);
  }
}

/* -------------------------- Verweise auf die rechtlichen Seiten */

/*
 * Wo ein Rechtstext eine andere rechtliche Seite nennt, fuehrt ein Verweis
 * direkt dorthin. Je Seite stehen die Wortlaute, unter denen sie im Text
 * auftaucht - gebeugte Formen zuerst, damit die kurze Form nicht mitten in
 * ihnen zuschlaegt -, dazu die Erkennung ihres Fusszeilen-Verweises und
 * Shopifys Standardpfad als Rueckfall.
 */
const SEITEN_VERWEISE = [
  {
    wortlaute: ['Impressum'],
    erkennung: /impressum/i,
    standard: '/policies/legal-notice',
  },
  {
    wortlaute: ['Allgemeinen Geschäftsbedingungen', 'Allgemeine Geschäftsbedingungen', 'AGB'],
    erkennung: /agb|geschäftsbedingungen/i,
    standard: '/policies/terms-of-service',
  },
  {
    wortlaute: ['Datenschutzerklärung'],
    erkennung: /datenschutz/i,
    standard: '/policies/privacy-policy',
  },
  {
    wortlaute: ['Widerrufsrechts', 'Widerrufsrecht'],
    erkennung: /widerruf/i,
    standard: '/policies/refund-policy',
  },
];

/**
 * Die Adresse einer rechtlichen Seite - aus der Fusszeile gelesen, damit
 * sie nicht hart im Skript steht; faellt auf Shopifys Standardpfad zurueck.
 *
 * @param {RegExp} erkennung
 * @param {string} standard
 * @returns {string}
 */
function seitenAdresse(erkennung, standard) {
  const verweis = [...document.querySelectorAll('a[href*="/policies/"]')].find((a) =>
    erkennung.test(a.textContent)
  );
  return verweis ? verweis.getAttribute('href') : standard;
}

/**
 * Setzt den Markennamen in allen Textknoten unterhalb einer Wurzel in die
 * Akzentschrift. Laesst sich gefahrlos wiederholen: bereits gesetzte
 * Marken (Elternteil traegt dp-marke) werden uebersprungen.
 *
 * @param {Element} wurzel
 */
function verziere(wurzel) {
  /*
   * Erst sammeln, dann ersetzen: das Aufteilen der Knoten waehrend des Laufs
   * wuerde den Durchlauf durcheinanderbringen.
   */
  const lauf = document.createTreeWalker(wurzel, NodeFilter.SHOW_TEXT);
  const treffer = [];

  let knoten;
  while ((knoten = lauf.nextNode())) {
    if (knoten.parentElement && knoten.parentElement.closest('.dp-marke')) continue;
    if (naechsteStelle(knoten.textContent) >= 0) treffer.push(knoten);
  }

  for (const gefunden of treffer) zeichneAus(gefunden);
}

/* ---------------- Verweise auf Widerruf und Cookie-Einstellungen */

/*
 * Auch die gebeugte Form fuehrt zum Formular - "einen Vertrag zu
 * widerrufen" ist im Widerrufsrecht sogar die haeufigere. Die laengere
 * Wendung steht zuerst, damit die kurze nicht mitten in ihr zuschlaegt.
 */
const WIDERRUF_WORTLAUTE = ['Vertrag zu widerrufen', 'Vertrag widerrufen'];

/*
 * "Cookie-Einstellungen" nimmt denselben Weg wie der Verweis in der
 * Fusszeile: die Anzeige-Marke in der Adresse wird von dp-cookie-banner.js
 * abgefangen und oeffnet das mittige Einstellungs-Fenster an Ort und
 * Stelle. Bleibt der Klick unabgefangen (die Banner-Schnittstelle fehlt),
 * fuehrt die Adresse zum leisen Banner auf der Startseite.
 */
const COOKIE_WORTLAUT = 'Cookie-Einstellungen';
const COOKIE_ZIEL = '/#shopifyReshowConsentBanner';

/**
 * Verlinkt jede frei stehende Fundstelle eines Wortlauts im Text eines
 * Knotens. Was der Verweis tut, bestimmt der Aufrufer ueber richteEin.
 *
 * @param {Text} knoten
 * @param {string} wortlaut
 * @param {(verweis: HTMLAnchorElement) => void} richteEin
 */
function verlinkeWortlaut(knoten, wortlaut, richteEin) {
  let rest = knoten;

  for (;;) {
    const text = rest.textContent;
    const stelle = text.indexOf(wortlaut);
    if (stelle < 0) return;

    const davor = text.slice(Math.max(0, stelle - 1), stelle);
    const danach = text.slice(stelle + wortlaut.length, stelle + wortlaut.length + 1);
    if (/[a-zäöüß]/i.test(davor) || /[a-zäöüß]/i.test(danach)) {
      rest = rest.splitText(stelle + wortlaut.length);
      continue;
    }

    const mitte = rest.splitText(stelle);
    rest = mitte.splitText(wortlaut.length);

    const verweis = document.createElement('a');
    verweis.textContent = mitte.textContent;
    richteEin(verweis);
    mitte.parentNode?.replaceChild(verweis, mitte);
  }
}

/**
 * Sucht alle Textknoten unterhalb des Feldes, die den Wortlaut tragen und
 * nicht schon in einem Verweis stehen, und verlinkt die Fundstellen.
 *
 * @param {Element} feld
 * @param {string} wortlaut
 * @param {(verweis: HTMLAnchorElement) => void} richteEin
 */
function verlinkeUeberall(feld, wortlaut, richteEin) {
  const lauf = document.createTreeWalker(feld, NodeFilter.SHOW_TEXT);
  const treffer = [];

  let knoten;
  while ((knoten = lauf.nextNode())) {
    if (knoten.textContent.indexOf(wortlaut) < 0) continue;
    if (knoten.parentElement && knoten.parentElement.closest('a')) continue;
    treffer.push(knoten);
  }

  for (const gefunden of treffer) verlinkeWortlaut(gefunden, wortlaut, richteEin);
}

function starte() {
  /*
   * Auch der Cookie-Banner und sein Einstellungs-Fenster tragen die Marke
   * in der Akzentschrift - wie die rechtlichen Seiten. Shopify baut beide
   * erst bei Bedarf in die Seite, und ihre Texte kommen aus dem
   * Adminbereich; der Beobachter setzt die Marke nach jedem Aufbau nach.
   */
  const verziereBanner = () => {
    for (const kennung of ['#shopify-pc__banner', '#shopify-pc__prefs__dialog']) {
      const kasten = document.querySelector(kennung);
      if (kasten) verziere(kasten);
    }
  };

  new MutationObserver(verziereBanner).observe(document.body, {
    childList: true,
    subtree: true,
  });
  verziereBanner();

  /*
   * Behandelt werden die Shopify-Rechtsseiten, eigene Seiten mit einem
   * dp-rechtstext-Kasten (etwa die Garantie) und - seit die Rechtstexte
   * auch als Fenster in der Fusszeile stehen - jedes dieser Fenster.
   * Deshalb alle Vorkommen, nicht nur das erste.
   */
  const felder = [...document.querySelectorAll('.shopify-policy__body, .dp-rechtstext')];
  if (!felder.length) return;

  /*
   * Auf den eigenen Rechtstext-Seiten traegt auch die Seitenueberschrift
   * die Marke - "2 Jahre DivinePrint Garantie" stuende sonst halb in der
   * Grundschrift, halb (im Text darunter) in der Wortmarke. Nur auf der
   * Seite selbst, nicht wegen eines Fensters in der Fusszeile.
   */
  const eigeneSeite = document.querySelector('main .dp-rechtstext, .shopify-policy__body');
  if (eigeneSeite && !eigeneSeite.closest('dialog')) {
    const kopf = document.querySelector('h1');
    if (kopf) verziere(kopf);
  }

  /*
   * Bewusst ueber das data-Merkmal, nicht ueber die Klasse: die Knoepfe
   * "Vertrag widerrufen" und "Fehler melden" in der Fusszeile teilen sich
   * die Stilklasse dp-footer__widerruf. Ueber sie angesprochen kam der
   * erste im Dokument - und das ist die Fehlermeldung.
   */
  const hatWiderrufsknopf = Boolean(document.querySelector('[data-dp-widerruf-oeffner]'));

  /** Vergleichbar machen: ohne Abfrage, ohne Marke, ohne Schlussstrich. */
  const pfadVon = (adresse) =>
    String(adresse || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/$/, '');

  for (const feld of felder) {
    verziere(feld);

    /*
     * Welcher Rechtstext steht hier gerade? Auf der Seite ist es deren
     * Adresse, in einem Fenster die des Fensters. Auf den eigenen Text
     * wird nie verlinkt - im Widerrufsrecht war sonst das Wort
     * "Widerrufsrecht" ein Verweis auf sich selbst.
     */
    const fenster = feld.closest('dialog[data-dp-rechtstext]');
    const eigenerPfad = pfadVon(
      fenster ? fenster.dataset.dpRechtstext : window.location.pathname
    );

    /*
     * Zweiter Durchlauf: Wo der Text eine andere rechtliche Seite nennt -
     * "siehe Impressum", "unsere AGB", "in der Datenschutzerklärung", "das
     * Widerrufsrecht" -, fuehrt ab jetzt ein Verweis direkt dorthin.
     * Bestehende Verweise bleiben unberuehrt.
     */
    for (const seite of SEITEN_VERWEISE) {
      const ziel = seitenAdresse(seite.erkennung, seite.standard);
      if (eigenerPfad === pfadVon(ziel)) continue;

      for (const wortlaut of seite.wortlaute) {
        verlinkeUeberall(feld, wortlaut, (verweis) => {
          verweis.href = ziel;
        });
      }
    }

    /*
     * Dritter Durchlauf: "Cookie-Einstellungen" fuehrt direkt zum mittigen
     * Einstellungs-Fenster - wie der gleichnamige Verweis in der Fusszeile.
     */
    verlinkeUeberall(feld, COOKIE_WORTLAUT, (verweis) => {
      verweis.href = COOKIE_ZIEL;
    });

    /*
     * Vierter Durchlauf: "Vertrag widerrufen" fuehrt direkt zur
     * Online-Widerrufsfunktion - der Verweis oeffnet den Dialog aus der
     * Fusszeile. Nur wenn es dessen Knopf auf der Seite gibt.
     */
    if (!hatWiderrufsknopf) continue;

    for (const wortlaut of WIDERRUF_WORTLAUTE) {
      verlinkeUeberall(feld, wortlaut, (verweis) => {
        verweis.href = '#';
        verweis.addEventListener('click', (ereignis) => {
          ereignis.preventDefault();
          /*
           * Steht der Wortlaut in einem Rechtstext-Fenster, tritt dieses
           * ab - sonst laegen zwei Fenster uebereinander.
           */
          verweis.closest('dialog')?.close();
          document.querySelector('[data-dp-widerruf-oeffner]')?.click();
        });
      });
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte, { once: true });
} else {
  starte();
}
